import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useCallback, useState } from 'react'
import { motion, useMotionValueEvent, useScroll } from 'motion/react'
import { cn } from '@/lib/utils'
import { EASE_OUT_EXPO } from '@/components/motion/reveal'
import { Magnetic } from '@/components/motion/magnetic'
import { Logo } from '@/components/generals/logo'
import { MenuOverlay } from '@/components/generals/menu-overlay'
import { OfficeClocks } from '@/components/generals/office-clocks'

export const Header: FC = (): ReactNode => {
    const [open, setOpen] = useState(false)
    const [hidden, setHidden] = useState(false)
    const { scrollY } = useScroll()

    useMotionValueEvent(scrollY, 'change', latest => {
        const previous = scrollY.getPrevious() ?? 0

        setHidden(latest > previous && latest > 200)
    })

    const close = useCallback(() => setOpen(false), [])

    return (
        <>
            <motion.header
                className={cn('fixed inset-x-0 top-0 z-50 transition-colors duration-500', open ? 'text-mist' : 'text-tone')}
                animate={{ y: hidden && !open ? '-110%' : '0%' }}
                transition={{ duration: 0.7, ease: EASE_OUT_EXPO }}
            >
                <div className='flex h-20 items-center justify-between gap-6 px-5 md:px-8 lg:h-24 lg:px-12'>
                    <Link href='/' aria-label='nSure.ai home' className='relative z-10 block shrink-0' onClick={close}>
                        <Logo className='h-6 w-auto lg:h-7' />
                    </Link>

                    <div className='hidden items-center gap-3 xl:flex'>
                        <span className='relative flex size-2' aria-hidden>
                            <span className='absolute inset-0 rounded-full bg-teal [animation:pulse-ring_2.4s_ease-out_infinite]' />
                            <span className='relative size-2 rounded-full bg-teal' />
                        </span>
                        <span className='t-label text-tone-muted'>
                            Live decisioning
                        </span>
                        <OfficeClocks className='ml-3' />
                    </div>

                    <div className='flex items-center gap-2'>
                        <Magnetic strength={0.25} className='hidden sm:block'>
                            <Link
                                href='/contact'
                                data-cursor='Book'
                                className='group relative flex h-11 items-center gap-2.5 overflow-hidden rounded-full bg-violet pl-5 pr-4 text-sm font-semibold text-white'
                            >
                                <span className='absolute inset-0 translate-y-full rounded-full bg-mist transition-transform duration-500 ease-out-expo group-hover:translate-y-0' aria-hidden />
                                <span className='relative transition-colors duration-500 group-hover:text-void'>
                                    Book a call
                                </span>
                                <span className='relative size-1.5 rounded-full bg-teal' aria-hidden />
                            </Link>
                        </Magnetic>

                        <button
                            id='menu-toggle'
                            type='button'
                            aria-expanded={open}
                            aria-controls='site-menu'
                            onClick={() => setOpen(value => !value)}
                            className={cn(
                                'group flex h-11 items-center gap-3 rounded-full border px-5 text-sm font-semibold transition-colors duration-500',
                                open ? 'border-mist/25 hover:bg-mist hover:text-void' : 'border-tone-line hover:bg-tone hover:text-tone-bg'
                            )}
                        >
                            <span className='relative block h-4 w-12 overflow-hidden'>
                                <span className={cn('absolute inset-0 transition-transform duration-500 ease-out-expo', open && '-translate-y-full')}>
                                    Menu
                                </span>
                                <span className={cn('absolute inset-0 translate-y-full transition-transform duration-500 ease-out-expo', open && 'translate-y-0')}>
                                    Close
                                </span>
                            </span>
                            <span className='relative flex h-3 w-4 flex-col justify-between' aria-hidden>
                                <span className={cn('h-px w-full bg-current transition-transform duration-500 ease-out-expo', open && 'translate-y-[5.5px] rotate-45')} />
                                <span className={cn('h-px w-full bg-current transition-transform duration-500 ease-out-expo', open && '-translate-y-[5.5px] -rotate-45')} />
                            </span>
                        </button>
                    </div>
                </div>
            </motion.header>

            <MenuOverlay open={open} onClose={close} />
        </>
    )
}
